import {
  mountBrightcoveBackgroundPlayer,
  type BrightcoveBackgroundHandle
} from './shared-brightcove-embed';

type UserStoryVideoState = {
  expandedIndex: number;
  inView: boolean;
  pageVisible: boolean;
  prefersReducedMotion: boolean;
  storyIndex: number;
};

export function shouldPlayUserStoryVideo({
  expandedIndex,
  inView,
  pageVisible,
  prefersReducedMotion,
  storyIndex
}: UserStoryVideoState): boolean {
  return storyIndex === expandedIndex && inView && pageVisible && !prefersReducedMotion;
}

export function wrapUserStoryIndex(index: number, slideCount: number): number {
  if (slideCount <= 0) {
    return 0;
  }

  return ((index % slideCount) + slideCount) % slideCount;
}

export function canLoopUserStories(slideCount: number): boolean {
  return slideCount > 1;
}

export function isUserStoryInVisibleRange(
  trackIndex: number,
  selectedIndex: number,
  visibleCount: number
): boolean {
  return trackIndex >= selectedIndex && trackIndex < selectedIndex + visibleCount;
}

export type UserStoryHoverLayout = {
  neighborIndexes: number[];
  shrinkDivisors: number[];
};

export function getUserStoryHoverLayout(
  visibleCount: number,
  hoveredVisibleIndex: number
): UserStoryHoverLayout {
  if (visibleCount <= 1 || hoveredVisibleIndex < 0 || hoveredVisibleIndex >= visibleCount) {
    return { neighborIndexes: [], shrinkDivisors: [] };
  }

  const leftNeighborIndexes = Array.from({ length: hoveredVisibleIndex }, (_, index) => index);
  const rightNeighborIndexes = Array.from(
    { length: visibleCount - hoveredVisibleIndex - 1 },
    (_, index) => hoveredVisibleIndex + index + 1
  );
  const sideShare = leftNeighborIndexes.length > 0 && rightNeighborIndexes.length > 0 ? 2 : 1;
  const shrinkDivisors = Array.from({ length: visibleCount }, () => 0);

  leftNeighborIndexes.forEach(index => {
    shrinkDivisors[index] = sideShare * leftNeighborIndexes.length;
  });
  rightNeighborIndexes.forEach(index => {
    shrinkDivisors[index] = sideShare * rightNeighborIndexes.length;
  });

  return {
    neighborIndexes: [...leftNeighborIndexes, ...rightNeighborIndexes],
    shrinkDivisors
  };
}

function setButtonState(button: HTMLButtonElement, disabled: boolean): void {
  button.disabled = disabled;
  button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
}

function whenDisconnected(element: HTMLElement, onDisconnect: () => void): void {
  const observer = new MutationObserver(() => {
    if (!element.isConnected) {
      observer.disconnect();
      onDisconnect();
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

const MOVE_DURATION_FALLBACK_MS = 700;
const SWIPE_THRESHOLD_PX = 28;
const SWIPE_INTENT_PX = 8;
const POINTER_SWIPE_THRESHOLD_PX = 48;

type SwipeAxis = 'undecided' | 'horizontal' | 'vertical';
const DESKTOP_VISIBLE_COUNT = 4;
const DESKTOP_OVERFLOW_COUNT = DESKTOP_VISIBLE_COUNT - 1;
const TRANSFORM_TRANSITION =
  'transform var(--user-stories-move-duration) var(--user-stories-move-easing)';
const SIZE_TRANSITION =
  'flex-basis var(--user-stories-move-duration) var(--user-stories-move-easing)';

function getMoveDurationMs(root: HTMLElement): number {
  const value = window
    .getComputedStyle(root)
    .getPropertyValue('--user-stories-move-duration')
    .trim();
  const duration = Number.parseFloat(value);

  if (!Number.isFinite(duration)) {
    return MOVE_DURATION_FALLBACK_MS;
  }

  return value.endsWith('s') && !value.endsWith('ms') ? duration * 1000 : duration;
}

function getTrackGap(track: HTMLElement): number {
  const value = Number.parseFloat(window.getComputedStyle(track).columnGap);

  return Number.isFinite(value) ? value : 0;
}

function waitForTransform(track: HTMLElement, durationMs: number): Promise<void> {
  return new Promise(resolve => {
    let settled = false;
    const finish = (): void => {
      if (settled) {
        return;
      }

      settled = true;
      track.removeEventListener('transitionend', onEnd);
      window.clearTimeout(timeoutId);
      resolve();
    };
    const onEnd = (event: TransitionEvent): void => {
      if (event.target === track && event.propertyName === 'transform') {
        finish();
      }
    };
    const timeoutId = window.setTimeout(finish, durationMs + 80);

    track.addEventListener('transitionend', onEnd);
  });
}

function lockSlideWidth(slide: HTMLElement, width: number, animate: boolean): void {
  slide.style.transition = animate ? SIZE_TRANSITION : 'none';
  slide.style.flexBasis = `${width}px`;
}

function clearSlideWidth(slide: HTMLElement): void {
  slide.style.removeProperty('transition');
  slide.style.removeProperty('flex-basis');
}

function measureDesktopSlotWidths(slides: HTMLElement[]): number[] {
  const widths = slides
    .slice(0, DESKTOP_VISIBLE_COUNT)
    .map(slide => slide.getBoundingClientRect().width);
  const fallback = widths.at(-1) ?? 0;

  while (widths.length < DESKTOP_VISIBLE_COUNT) {
    widths.push(fallback);
  }

  return widths;
}

function promoteSlideImages(slides: HTMLElement[]): void {
  slides.forEach(slide => {
    slide.querySelectorAll('img').forEach(image => {
      if (image.loading === 'eager') {
        return;
      }

      image.loading = 'eager';
      void image.decode().catch(() => undefined);
    });
  });
}

type UserStoryImagePreloadState = {
  inView: boolean;
  selectedIndex: number;
  trackIndex: number;
  visibleCount: number;
};

export function shouldPreloadUserStorySlideImage({
  inView,
  selectedIndex,
  trackIndex,
  visibleCount
}: UserStoryImagePreloadState): boolean {
  return (
    inView && isUserStoryInVisibleRange(trackIndex, selectedIndex, Math.max(visibleCount, 0) + 1)
  );
}

function isCloneSlide(slide: HTMLElement): boolean {
  return slide.hasAttribute('data-carousel-clone');
}

function createOverflowClone(source: HTMLElement): HTMLElement {
  const clone = source.cloneNode(true) as HTMLElement;

  clone.dataset.carouselClone = 'true';
  clone.dataset.expanded = 'false';
  clone.removeAttribute('data-selected');
  clone.removeAttribute('data-hover-preview');
  clone.removeAttribute('data-hover-neighbor');
  clone.setAttribute('aria-hidden', 'true');
  clone.inert = true;
  clone.querySelector('[data-user-story-player]')?.remove();
  clone.querySelectorAll('[data-user-story-card]').forEach(card => {
    card.removeAttribute('role');
    card.removeAttribute('tabindex');
    card.removeAttribute('aria-expanded');
    card.removeAttribute('aria-label');
  });
  clone.querySelectorAll('img').forEach(image => {
    image.loading = 'lazy';
  });

  return clone;
}

export default function userStoriesCarousel(root: HTMLElement): void {
  const viewport = root.querySelector<HTMLElement>('[data-carousel-viewport]');
  const track = root.querySelector<HTMLElement>('[data-carousel-container]');
  const prevButton = root.querySelector<HTMLButtonElement>('[data-carousel-prev]');
  const nextButton = root.querySelector<HTMLButtonElement>('[data-carousel-next]');
  const dotsRoot = root.querySelector<HTMLElement>('[data-carousel-dots]');
  const controls = root.querySelector<HTMLElement>('[data-carousel-controls]');
  const status = root.querySelector<HTMLElement>('[data-carousel-status]');

  if (!viewport || !track || !prevButton || !nextButton || !dotsRoot || !controls || !status) {
    return;
  }

  const trackEl = track;
  const viewportEl = viewport;
  const moveDurationMs = getMoveDurationMs(root);
  const getTrackSlides = (): HTMLElement[] => [
    ...trackEl.querySelectorAll<HTMLElement>('[data-carousel-slide]')
  ];

  const getRealSlides = (): HTMLElement[] => getTrackSlides().filter(slide => !isCloneSlide(slide));

  if (getTrackSlides().length === 0) {
    root.hidden = true;
    return;
  }

  const desktopMedia = window.matchMedia('(width >= 1024px)');
  const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
  const lifecycle = new AbortController();
  const { signal } = lifecycle;
  const videoHandles = new Map<number, BrightcoveBackgroundHandle>();
  let hoveredStoryIndex: number | null = null;
  let inView = false;
  let pageVisible = !document.hidden;
  let disposed = false;
  let isAnimating = false;
  let pendingSteps = 0;
  let dots: HTMLButtonElement[] = [];

  const getStoryIndex = (slide: HTMLElement | undefined): number =>
    Number.parseInt(slide?.dataset.storyIndex ?? '0', 10) || 0;

  const getActiveStoryIndex = (): number => getStoryIndex(getRealSlides()[0]);

  const canLoop = (): boolean => canLoopUserStories(getRealSlides().length);

  const syncSlideImageLoading = (selectedIndex = 0): void => {
    const visibleCount = desktopMedia.matches ? DESKTOP_VISIBLE_COUNT : 1;
    const slidesToPreload = getTrackSlides().filter((_, trackIndex) =>
      shouldPreloadUserStorySlideImage({
        inView,
        selectedIndex,
        trackIndex,
        visibleCount
      })
    );

    promoteSlideImages(slidesToPreload);
  };

  const removeOverflowClones = (): void => {
    getTrackSlides().forEach(slide => {
      if (isCloneSlide(slide)) {
        slide.remove();
      }
    });
  };

  const fillOverflowClones = (minimumTrackLength = 0): void => {
    if (!desktopMedia.matches) {
      return;
    }

    const sources = getRealSlides();

    if (sources.length < 2) {
      return;
    }

    const targetLength = Math.max(
      DESKTOP_VISIBLE_COUNT + DESKTOP_OVERFLOW_COUNT,
      minimumTrackLength
    );
    let cloneIndex = getTrackSlides().filter(isCloneSlide).length;

    while (getTrackSlides().length < targetLength) {
      const source = sources[cloneIndex % sources.length];

      if (!source) {
        break;
      }

      trackEl.append(createOverflowClone(source));
      cloneIndex += 1;

      if (cloneIndex > targetLength + sources.length) {
        break;
      }
    }
  };

  const syncOverflowClones = (minimumTrackLength = 0): void => {
    if (!desktopMedia.matches || getRealSlides().length < 2) {
      removeOverflowClones();
      return;
    }

    removeOverflowClones();
    fillOverflowClones(minimumTrackLength);
  };

  const pauseVideo = (storyIndex: number): void => {
    videoHandles.get(storyIndex)?.pause();
    trackEl
      .querySelector<HTMLElement>(
        `[data-carousel-slide]:not([data-carousel-clone])[data-story-index="${storyIndex}"] [data-user-story-card]`
      )
      ?.setAttribute('data-video-playing', 'false');
  };

  const ensureVideoHandle = (
    storyIndex: number,
    playerHost: HTMLElement,
    card: HTMLElement
  ): BrightcoveBackgroundHandle | undefined => {
    let handle = videoHandles.get(storyIndex);

    if (handle) {
      return handle;
    }

    handle =
      mountBrightcoveBackgroundPlayer(playerHost, {
        autoplay: false,
        frameClassName: 'user-story-card__video-frame video-js vjs-fill',
        loop: true,
        playbackRate: 0.7,
        onPlayingChange: playing => {
          const isStillActive =
            !disposed &&
            shouldPlayUserStoryVideo({
              expandedIndex: getActiveStoryIndex(),
              inView,
              pageVisible,
              prefersReducedMotion: reducedMotionMedia.matches,
              storyIndex
            });
          card.dataset.videoPlaying = playing && isStillActive ? 'true' : 'false';
        }
      }) ?? undefined;

    if (handle) {
      videoHandles.set(storyIndex, handle);
    }

    return handle;
  };

  const syncVideoPlayback = (): void => {
    const expandedIndex = getActiveStoryIndex();

    getRealSlides().forEach(slide => {
      const storyIndex = getStoryIndex(slide);
      const card = slide.querySelector<HTMLElement>('[data-user-story-card]');
      const playerHost = slide.querySelector<HTMLElement>('[data-user-story-player]');
      const shouldPlay = shouldPlayUserStoryVideo({
        expandedIndex,
        inView,
        pageVisible,
        prefersReducedMotion: reducedMotionMedia.matches,
        storyIndex
      });

      if (!card || !playerHost || !shouldPlay) {
        pauseVideo(storyIndex);
        return;
      }

      const handle = ensureVideoHandle(storyIndex, playerHost, card);

      if (!handle) {
        return;
      }

      void handle
        .play()
        .then(() => {
          const isStillActive = shouldPlayUserStoryVideo({
            expandedIndex: getActiveStoryIndex(),
            inView,
            pageVisible,
            prefersReducedMotion: reducedMotionMedia.matches,
            storyIndex
          });

          if (!isStillActive) {
            pauseVideo(storyIndex);
          }
        })
        .catch(() => {
          card.dataset.videoPlaying = 'false';
        });
    });
  };

  const setTrackOffset = (offset: number, animate: boolean): void => {
    trackEl.style.transition = animate ? TRANSFORM_TRANSITION : 'none';
    trackEl.style.transform = `translate3d(${offset}px, 0, 0)`;
  };

  const resetTrackTransform = (): void => {
    trackEl.style.transition = 'none';
    trackEl.style.transform = 'translate3d(0, 0, 0)';
    void trackEl.offsetWidth;
    trackEl.style.removeProperty('transition');
    trackEl.style.removeProperty('transform');
  };

  const syncExpandedState = (): void => {
    const trackSlides = getTrackSlides();
    const activeStoryIndex = getActiveStoryIndex();
    const visibleCount = desktopMedia.matches ? DESKTOP_VISIBLE_COUNT : 1;
    const hasHoverPreview =
      desktopMedia.matches && hoveredStoryIndex !== null && hoveredStoryIndex !== activeStoryIndex;
    const visibleRealSlides = trackSlides.filter(
      (slide, trackIndex) =>
        !isCloneSlide(slide) && isUserStoryInVisibleRange(trackIndex, 0, visibleCount)
    );
    const hoveredVisibleIndex = hasHoverPreview
      ? visibleRealSlides.findIndex(slide => getStoryIndex(slide) === hoveredStoryIndex)
      : -1;
    const hoverLayout = getUserStoryHoverLayout(visibleRealSlides.length, hoveredVisibleIndex);
    const hoverNeighbors = new Map(
      hoverLayout.neighborIndexes.map(index => [
        visibleRealSlides[index],
        hoverLayout.shrinkDivisors[index]
      ])
    );

    root.toggleAttribute('data-has-hover-preview', hasHoverPreview);
    if (hoveredVisibleIndex >= 0) {
      root.dataset.hoverSlot = String(hoveredVisibleIndex + 1);
    } else {
      delete root.dataset.hoverSlot;
    }

    trackSlides.forEach((slide, trackIndex) => {
      const card = slide.querySelector<HTMLElement>('[data-user-story-card]');
      const cardBody = slide.querySelector<HTMLElement>('[data-user-story-body]');
      const storyTitle = card?.dataset.storyTitle || `Story ${trackIndex + 1}`;
      const storyIndex = getStoryIndex(slide);
      const isClone = isCloneSlide(slide);
      const expanded = !isClone && trackIndex === 0;
      const inVisibleRange = isUserStoryInVisibleRange(trackIndex, 0, visibleCount);
      const shrinkDivisor = hoverNeighbors.get(slide);

      slide.dataset.expanded = expanded ? 'true' : 'false';
      slide.dataset.selected = expanded ? 'true' : 'false';
      slide.toggleAttribute(
        'data-hover-preview',
        !isClone && hasHoverPreview && storyIndex === hoveredStoryIndex
      );
      slide.toggleAttribute(
        'data-hover-neighbor',
        !isClone && hasHoverPreview && inVisibleRange && shrinkDivisor !== undefined
      );
      if (shrinkDivisor) {
        slide.style.setProperty(
          '--user-stories-slide-hover-shrink',
          `calc(var(--user-stories-hover-growth) / ${shrinkDivisor})`
        );
      } else {
        slide.style.removeProperty('--user-stories-slide-hover-shrink');
      }

      if (cardBody) {
        cardBody.inert = !expanded;
        cardBody.setAttribute('aria-hidden', expanded ? 'false' : 'true');
      }

      if (card) {
        if (desktopMedia.matches && !expanded && !isClone) {
          card.setAttribute('role', 'button');
          card.setAttribute('tabindex', '0');
          card.setAttribute('aria-expanded', 'false');
          card.setAttribute('aria-label', `Show story: ${storyTitle}`);
        } else {
          card.removeAttribute('role');
          card.removeAttribute('tabindex');
          card.removeAttribute('aria-expanded');
          card.removeAttribute('aria-label');
        }
      }
    });

    syncSlideImageLoading();
    syncVideoPlayback();
  };

  const renderDots = (): void => {
    const slideCount = getRealSlides().length;
    dotsRoot.replaceChildren();
    dots = Array.from({ length: slideCount }, (_, index) => {
      const dot = document.createElement('button');
      const dotCore = document.createElement('span');
      dot.type = 'button';
      dot.className = 'carousel-controls__dot';
      dot.dataset.carouselDot = String(index);
      dot.setAttribute('aria-label', `Go to story ${index + 1} of ${slideCount}`);
      dotCore.className = 'carousel-controls__dot-core';
      dot.append(dotCore);
      dot.addEventListener(
        'click',
        () => {
          void navigateToStory(index);
        },
        { signal }
      );
      dotsRoot.append(dot);
      return dot;
    });
  };

  const syncControls = (): void => {
    const slideCount = getRealSlides().length;
    const looping = canLoop();
    const activeStoryIndex = getActiveStoryIndex();

    setButtonState(prevButton, !looping);
    setButtonState(nextButton, !looping);
    controls.hidden = !looping;

    dots.forEach((dot, index) => {
      const active = index === activeStoryIndex;
      dot.toggleAttribute('data-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
      dot.setAttribute('aria-disabled', active ? 'true' : 'false');
    });

    status.textContent = `Story ${activeStoryIndex + 1} of ${slideCount}`;
  };

  const clearHoverPreview = (): void => {
    if (hoveredStoryIndex === null) {
      return;
    }

    hoveredStoryIndex = null;
    syncExpandedState();
  };

  const finishMove = (): void => {
    resetTrackTransform();
    root.removeAttribute('data-animating');
    isAnimating = false;
    hoveredStoryIndex = null;
    syncOverflowClones();
    syncExpandedState();
    syncControls();

    if (pendingSteps !== 0) {
      const steps = pendingSteps;
      pendingSteps = 0;
      requestAnimationFrame(() => {
        void moveBy(steps);
      });
    }
  };

  const settleAfterNext = (outgoing: HTMLElement[], ...sizedSlides: HTMLElement[]): void => {
    trackEl.style.transition = 'none';
    const reals = outgoing.filter(slide => !isCloneSlide(slide));

    if (desktopMedia.matches) {
      reals.forEach(slide => {
        const clone = getTrackSlides().find(
          candidate =>
            isCloneSlide(candidate) && candidate.dataset.storyIndex === slide.dataset.storyIndex
        );

        if (clone) {
          clone.replaceWith(slide);
          return;
        }

        trackEl.append(slide);
      });
    } else {
      reals.forEach(slide => trackEl.append(slide));
    }

    trackEl.style.transform = 'translate3d(0, 0, 0)';
    sizedSlides.forEach(clearSlideWidth);
    void trackEl.offsetWidth;
    finishMove();
  };

  const animateNext = async (steps = 1): Promise<void> => {
    const realCount = getRealSlides().length;
    const moveCount = Math.min(steps, Math.max(realCount - 1, 0));

    if (desktopMedia.matches) {
      fillOverflowClones(moveCount + DESKTOP_VISIBLE_COUNT + 1);
    }

    syncSlideImageLoading(moveCount);

    const slides = getTrackSlides();
    const outgoing = slides.slice(0, moveCount);
    const destination = slides[moveCount];
    const current = outgoing[0];

    if (moveCount < 1 || !current || !destination || outgoing.length !== moveCount) {
      return;
    }

    isAnimating = true;
    root.dataset.animating = 'true';
    clearHoverPreview();

    if (reducedMotionMedia.matches) {
      outgoing.forEach(slide => trackEl.append(slide));
      finishMove();
      return;
    }

    const gap = getTrackGap(trackEl);

    if (!desktopMedia.matches) {
      const offset = outgoing.reduce(
        (distance, slide) => distance + slide.getBoundingClientRect().width + gap,
        0
      );
      setTrackOffset(-offset, true);
      await waitForTransform(trackEl, moveDurationMs);
      settleAfterNext(outgoing);
      return;
    }

    const slotWidths = measureDesktopSlotWidths(slides);
    const currentWidths = slides.map(slide => slide.getBoundingClientRect().width);
    const sizedSlides: HTMLElement[] = [];
    const lock = (slide: HTMLElement | undefined, width: number, animate: boolean): void => {
      if (!slide) {
        return;
      }

      lockSlideWidth(slide, width, animate);
      sizedSlides.push(slide);
    };

    slides.forEach((slide, index) => {
      lock(slide, currentWidths[index] ?? 0, false);
    });
    void trackEl.offsetWidth;

    destination.dataset.expanded = 'true';
    outgoing.forEach(slide => {
      slide.dataset.expanded = 'false';
    });

    const outgoingFinalWidths = outgoing.map((slide, index) => {
      const width = index === 0 ? (slotWidths[1] ?? 0) : (currentWidths[index] ?? 0);
      lock(slide, width, true);
      return width;
    });

    for (let slot = 0; slot < DESKTOP_VISIBLE_COUNT; slot += 1) {
      lock(slides[moveCount + slot], slotWidths[slot] ?? 0, true);
    }

    const offset = outgoingFinalWidths.reduce((distance, width) => distance + width + gap, 0);
    setTrackOffset(-offset, true);

    await waitForTransform(trackEl, moveDurationMs);
    settleAfterNext(outgoing, ...sizedSlides);
  };

  const animatePrev = async (): Promise<void> => {
    const slides = getTrackSlides();
    const incoming = getRealSlides().at(-1);
    const current = slides[0];

    if (!incoming || !current) {
      return;
    }

    if (inView) {
      promoteSlideImages([incoming]);
    }

    isAnimating = true;
    root.dataset.animating = 'true';
    clearHoverPreview();

    if (reducedMotionMedia.matches) {
      trackEl.prepend(incoming);
      finishMove();
      return;
    }

    const gap = getTrackGap(trackEl);

    if (!desktopMedia.matches) {
      trackEl.prepend(incoming);
      const offset = incoming.getBoundingClientRect().width + gap;
      setTrackOffset(-offset, false);
      void trackEl.offsetWidth;
      setTrackOffset(0, true);
      await waitForTransform(trackEl, moveDurationMs);
      finishMove();
      return;
    }

    const slotWidths = measureDesktopSlotWidths(slides);
    const currentWidths = slides.map(slide => slide.getBoundingClientRect().width);
    const incomingStartWidth = incoming.getBoundingClientRect().width;

    trackEl.prepend(incoming);
    lockSlideWidth(incoming, incomingStartWidth, false);
    slides.forEach((slide, index) => {
      if (slide === incoming) {
        return;
      }

      lockSlideWidth(slide, currentWidths[index] ?? 0, false);
    });
    setTrackOffset(-(incomingStartWidth + gap), false);
    void trackEl.offsetWidth;

    incoming.dataset.expanded = 'true';
    current.dataset.expanded = 'false';
    lockSlideWidth(incoming, slotWidths[0] ?? 0, true);

    const shiftedSlides = getTrackSlides();
    for (let slot = 1; slot < DESKTOP_VISIBLE_COUNT; slot += 1) {
      const slide = shiftedSlides[slot];

      if (slide) {
        lockSlideWidth(slide, slotWidths[slot] ?? 0, true);
      }
    }

    setTrackOffset(0, true);

    await waitForTransform(trackEl, moveDurationMs);
    getTrackSlides().forEach(clearSlideWidth);
    finishMove();
  };

  const moveBy = async (steps: number, queueWhenBusy = false): Promise<void> => {
    if (!canLoop() || steps === 0) {
      return;
    }

    if (isAnimating) {
      if (queueWhenBusy && Math.abs(steps) === 1) {
        pendingSteps = steps;
      }

      return;
    }

    const direction = Math.sign(steps);
    const count = Math.abs(steps);

    if (direction > 0) {
      await animateNext(count);
      return;
    }

    for (let index = 0; index < count; index += 1) {
      await animatePrev();
    }
  };

  const navigateToStory = async (storyIndex: number): Promise<void> => {
    const trackIndex = getRealSlides().findIndex(slide => getStoryIndex(slide) === storyIndex);

    if (trackIndex <= 0) {
      return;
    }

    await moveBy(trackIndex);
  };

  const rebuildForViewport = (): void => {
    hoveredStoryIndex = null;
    resetTrackTransform();
    root.removeAttribute('data-animating');
    isAnimating = false;
    syncOverflowClones();
    syncExpandedState();
    renderDots();
    syncControls();
  };

  getTrackSlides().forEach(slide => {
    const card = slide.querySelector<HTMLElement>('[data-user-story-card]');
    const title = card?.dataset.storyTitle || 'Story';
    const storyIndex = getStoryIndex(slide);
    slide.setAttribute('aria-label', `${storyIndex + 1} of ${getTrackSlides().length}: ${title}`);

    slide.addEventListener(
      'pointerenter',
      event => {
        if (!desktopMedia.matches || event.pointerType === 'touch' || isAnimating) {
          return;
        }

        hoveredStoryIndex = storyIndex === getActiveStoryIndex() ? null : storyIndex;
        syncExpandedState();
      },
      { signal }
    );

    slide.addEventListener(
      'pointerleave',
      () => {
        if (!isAnimating) {
          clearHoverPreview();
        }
      },
      { signal }
    );

    card?.addEventListener(
      'click',
      event => {
        if (event.target instanceof Element && event.target.closest('a')) {
          return;
        }

        void navigateToStory(getStoryIndex(slide));
      },
      { signal }
    );

    card?.addEventListener(
      'keydown',
      event => {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }

        event.preventDefault();
        void navigateToStory(getStoryIndex(slide));
      },
      { signal }
    );
  });

  prevButton.addEventListener(
    'click',
    () => {
      void moveBy(-1);
    },
    { signal }
  );
  nextButton.addEventListener(
    'click',
    () => {
      void moveBy(1);
    },
    { signal }
  );

  let swipe: { x: number; y: number; axis: SwipeAxis; committed: boolean } | null = null;

  const startSwipe = (x: number, y: number): void => {
    swipe = { x, y, axis: 'undecided', committed: false };
  };

  const endSwipe = (): void => {
    swipe = null;
  };

  const trackSwipe = (x: number, y: number): SwipeAxis => {
    if (!swipe) {
      return 'undecided';
    }

    const deltaX = x - swipe.x;
    const deltaY = y - swipe.y;

    if (
      swipe.axis === 'undecided' &&
      Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= SWIPE_INTENT_PX
    ) {
      swipe.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
    }

    if (swipe.axis === 'horizontal' && !swipe.committed && Math.abs(deltaX) >= SWIPE_THRESHOLD_PX) {
      swipe.committed = true;
      void moveBy(deltaX < 0 ? 1 : -1, !desktopMedia.matches);
    }

    return swipe.axis;
  };

  viewportEl.addEventListener(
    'touchstart',
    event => {
      const touch = event.touches[0];

      if (!touch || event.touches.length > 1) {
        endSwipe();
        return;
      }

      startSwipe(touch.clientX, touch.clientY);
    },
    { passive: true, signal }
  );

  viewportEl.addEventListener(
    'touchmove',
    event => {
      const touch = event.touches[0];

      if (!touch) {
        return;
      }

      if (trackSwipe(touch.clientX, touch.clientY) === 'horizontal' && event.cancelable) {
        event.preventDefault();
      }
    },
    { passive: false, signal }
  );

  viewportEl.addEventListener('touchend', endSwipe, { passive: true, signal });
  viewportEl.addEventListener('touchcancel', endSwipe, { passive: true, signal });

  let pointerStartX: number | null = null;

  viewportEl.addEventListener(
    'pointerdown',
    event => {
      if (event.pointerType === 'touch') {
        return;
      }

      pointerStartX = event.clientX;
    },
    { signal }
  );

  viewportEl.addEventListener(
    'pointerup',
    event => {
      if (event.pointerType === 'touch' || pointerStartX === null) {
        return;
      }

      const deltaX = event.clientX - pointerStartX;
      pointerStartX = null;

      if (Math.abs(deltaX) < POINTER_SWIPE_THRESHOLD_PX) {
        return;
      }

      void moveBy(deltaX < 0 ? 1 : -1);
    },
    { signal }
  );

  root.addEventListener(
    'keydown',
    event => {
      const target = event.target;

      if (
        !(target instanceof HTMLElement) ||
        !target.closest(
          '[data-carousel-prev], [data-carousel-next], [data-carousel-dot], [data-user-story-expand]'
        )
      ) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        void moveBy(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        void moveBy(1);
      }
    },
    { signal }
  );

  const visibilityObserver = new IntersectionObserver(
    entries => {
      const entry = entries[0];

      if (!entry || disposed) {
        return;
      }

      inView = entry.isIntersecting;
      syncSlideImageLoading();
      syncVideoPlayback();
    },
    { threshold: 0.25 }
  );

  const teardown = (): void => {
    if (disposed) {
      return;
    }

    disposed = true;
    lifecycle.abort();
    visibilityObserver.disconnect();
    videoHandles.forEach(handle => handle.dispose());
    videoHandles.clear();
  };

  document.addEventListener(
    'visibilitychange',
    () => {
      pageVisible = !document.hidden;
      syncVideoPlayback();
    },
    { signal }
  );
  window.addEventListener('pagehide', teardown, { signal });
  desktopMedia.addEventListener('change', rebuildForViewport, { signal });
  reducedMotionMedia.addEventListener('change', syncVideoPlayback, { signal });
  visibilityObserver.observe(root);
  whenDisconnected(root, teardown);

  syncOverflowClones();
  syncExpandedState();
  renderDots();
  syncControls();
}
